import React from 'react';
import {
  Box,
  Checkbox,
  Circle,
  Flex,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Link as ChakraLink,
  Spacer,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {
  Checkmark,
  Export,
  Grid as GridIcon,
  ImportExport,
  Search,
  Tag,
} from '@carbon/icons-react';
import {CompanyLogo} from '../Icons/CompanyLogo';
import {IntentIcon} from '../Icons/Intent';
import {HeaderButton} from '../AccountGrid/header/HeaderButton';
import {AccountTier} from '../../shared/scoredAccounts';

// Column definitions matching the freemium v3 intent view
const columns = [
  {id: 'select', header: '', width: 40},
  {id: 'account', header: 'Account', width: 200},
  {id: 'intentDate', header: 'Last Intent', width: 100},
  {id: 'icpFit', header: 'ICP Fit', width: 70},
  {id: 'intentTier', header: 'Intent Score', width: 110},
  {id: 'insight', header: 'Insight', width: 400},
  {id: 'intentSignals', header: 'Intent Signals', width: 400},
  {id: 'category', header: 'Categories', width: 250},
  {id: 'employees', header: 'Employees', width: 100},
  {id: 'hq', header: 'HQ Country', width: 100},
] as const;

type ColumnId = (typeof columns)[number]['id'];

interface MockCompany {
  name: string;
  domain: string;
  lastIntent: string;
  icpFit: AccountTier;
  intentTier: 'high' | 'medium' | 'low';
  intentScore: number;
  insight: string;
  intentSignals: string[];
  categories: string[];
  employees: number;
  hqCountry: string;
}

const mockCompanies: MockCompany[] = [
  {
    name: 'PandaDoc',
    domain: 'pandadoc.com',
    lastIntent: 'Jan 22, 2025',
    icpFit: 'A',
    intentTier: 'high',
    intentScore: 82,
    insight: 'Visited pricing page 3 times this week',
    intentSignals: ['Pricing Page', 'Case Studies'],
    categories: ['Document Management', 'SaaS'],
    employees: 1200,
    hqCountry: 'United States',
  },
  {
    name: 'Lattice',
    domain: 'lattice.com',
    lastIntent: 'Jan 19, 2025',
    icpFit: 'B',
    intentTier: 'medium',
    intentScore: 54,
    insight: 'Active in HR tech conferences and webinars',
    intentSignals: ['Job Postings', 'Content Publishing'],
    categories: ['HR Tech', 'SaaS'],
    employees: 800,
    hqCountry: 'United States',
  },
  {
    name: 'Vercel',
    domain: 'vercel.com',
    lastIntent: 'Jan 15, 2025',
    icpFit: 'A',
    intentTier: 'high',
    intentScore: 71,
    insight: 'Recently launched new enterprise features',
    intentSignals: ['Product Launch', 'Funding Round'],
    categories: ['Developer Tools', 'Cloud'],
    employees: 550,
    hqCountry: 'United States',
  },
  {
    name: 'Ramp',
    domain: 'ramp.com',
    lastIntent: 'Jan 12, 2025',
    icpFit: 'A',
    intentTier: 'medium',
    intentScore: 63,
    insight: 'Expanded engineering team by 40% this quarter',
    intentSignals: ['Tech Hiring', 'Web Traffic Up'],
    categories: ['Fintech', 'B2B'],
    employees: 1400,
    hqCountry: 'United States',
  },
];

const SignalPill = ({label}: {label: string}) => (
  <Box
    borderWidth="1px"
    borderColor="kgray.200"
    borderRadius="full"
    py={0.5}
    px={2}
    color="kgray.400"
    fontSize="xs"
    whiteSpace="nowrap"
  >
    {label}
  </Box>
);

const CategoryPill = ({label}: {label: string}) => (
  <Box
    borderWidth="1px"
    borderColor="kred.100"
    color="kred.300"
    fontSize="xs"
    px={2}
    py={0.5}
    borderRadius="full"
    whiteSpace="nowrap"
  >
    {label}
  </Box>
);

// Helper function to get column width by ID
const getColumnWidth = (id: ColumnId): number => {
  const column = columns.find((col) => col.id === id);
  return column?.width ?? 100;
};

type TableCellProps = Omit<
  React.ComponentPropsWithoutRef<typeof Td>,
  'width'
> & {
  id: ColumnId;
};

const TableCell = ({
  id,
  children,
  ...props
}: React.PropsWithChildren<TableCellProps>) => (
  <Td
    borderRight="1px solid #E8E8EB"
    p={2}
    verticalAlign="top"
    width={`${getColumnWidth(id)}px`}
    {...props}
  >
    {children}
  </Td>
);

const MockToolbar = () => (
  <Flex alignItems="center" px={4} gap={1} h="71px" flexShrink={0}>
    <Box color="kgray.300" mr={4} whiteSpace="nowrap">
      0 accounts
    </Box>
    <HeaderButton leftIcon={<Tag size={16} />} isDisabled>
      Add Tag
    </HeaderButton>
    <HeaderButton leftIcon={<Tag size={16} />} isDisabled>
      Remove Tags
    </HeaderButton>
    <Spacer />
    <HeaderButton leftIcon={<Export size={16} />} isDisabled>
      Export
    </HeaderButton>
    <HeaderButton leftIcon={<ImportExport size={16} />} isDisabled>
      Sort
    </HeaderButton>
    <HeaderButton isDisabled>Filter</HeaderButton>
    <HeaderButton isDisabled>
      <GridIcon size={16} />
    </HeaderButton>
    <InputGroup maxW="225px" ml={4}>
      <InputLeftElement pointerEvents="none">
        <Search size={16} />
      </InputLeftElement>
      <Input placeholder="Search accounts" isDisabled />
    </InputGroup>
  </Flex>
);

const MockTableHeader = () => (
  <Table sx={{tableLayout: 'fixed'}} width="100%">
    <Thead>
      <Tr>
        {columns.map((col) => (
          <Th
            key={col.id}
            width={`${col.width}px`}
            bg="white"
            p={0}
            whiteSpace="nowrap"
            position="sticky"
            top={0}
            zIndex={1}
            {...(col.id === 'select'
              ? {borderRight: 'none'}
              : {borderRight: '1px solid #E8E8EB'})}
          >
            <Box
              p={col.id === 'select' ? undefined : 2}
              px={col.id === 'select' ? 3 : undefined}
              py={col.id === 'select' ? 2 : undefined}
              borderBottom="1px solid #E8E8EB"
              textTransform={col.id === 'select' ? undefined : 'uppercase'}
            >
              {col.id === 'select' ? (
                <Checkbox isDisabled size="sm" />
              ) : (
                col.header
              )}
            </Box>
          </Th>
        ))}
      </Tr>
    </Thead>
  </Table>
);

const MockTableBody = () => (
  <Table sx={{tableLayout: 'fixed'}} width="100%">
    <Tbody>
      {mockCompanies.map((company) => (
        <Tr key={company.domain}>
          {/* Checkbox */}
          <TableCell id="select" borderRight="none">
            <Checkbox isDisabled size="sm" />
          </TableCell>

          {/* Account */}
          <TableCell id="account">
            <Flex alignItems="center" gap={2}>
              <CompanyLogo
                domain={company.domain}
                name={company.name}
                boxSize="24px"
                borderRadius="4px"
              />
              <Box overflow="hidden">
                <Text fontSize="sm" fontWeight={500} isTruncated>
                  {company.name}
                </Text>
                <Text fontSize="xs" color="kgray.300" isTruncated>
                  {company.domain}
                </Text>
              </Box>
            </Flex>
          </TableCell>

          {/* Last Intent */}
          <TableCell id="intentDate">
            <Text fontSize="sm">{company.lastIntent}</Text>
          </TableCell>

          {/* ICP Fit */}
          <TableCell id="icpFit">
            <Text fontSize="sm" fontWeight={500}>
              {company.icpFit}
            </Text>
          </TableCell>

          {/* Intent Score */}
          <TableCell id="intentTier">
            <Flex alignItems="center" gap={2}>
              <IntentIcon tier={company.intentTier} />
              <Text fontSize="sm">{company.intentScore}</Text>
            </Flex>
          </TableCell>

          {/* Insight */}
          <TableCell id="insight">
            <Text fontSize="sm" color="kgray.400" isTruncated>
              {company.insight}
            </Text>
          </TableCell>

          {/* Intent Signals */}
          <TableCell id="intentSignals">
            <Flex gap={1} flexWrap="wrap">
              {company.intentSignals.map((signal) => (
                <SignalPill key={signal} label={signal} />
              ))}
            </Flex>
          </TableCell>

          {/* Categories */}
          <TableCell id="category">
            <Flex gap={1} flexWrap="wrap">
              {company.categories.map((cat) => (
                <CategoryPill key={cat} label={cat} />
              ))}
            </Flex>
          </TableCell>

          {/* Employees */}
          <TableCell id="employees">
            <Text fontSize="sm">{company.employees.toLocaleString()}</Text>
          </TableCell>

          {/* HQ Country */}
          <TableCell id="hq">
            <Text fontSize="sm">{company.hqCountry}</Text>
          </TableCell>
        </Tr>
      ))}
    </Tbody>
  </Table>
);

export const FreemiumV3EmptyState = () => (
  <Flex direction="column" h="100%">
    {/* Page header */}
    <Flex
      alignItems="baseline"
      bgColor="kgray.100"
      borderBottom="1px solid"
      borderBottomColor="kgray.200"
      height="100px"
      p={5}
    >
      <Box>
        <Box fontSize="2xl" mb={2}>
          We&apos;re populating your engaged accounts.
        </Box>
        <Box color="kgray.300" display={{base: 'none', lg: 'block'}}>
          Filter & export your accounts here.{' '}
          <ChakraLink
            color="kblue.300"
            href="https://keyplay.io/pricing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Upgrade
          </ChakraLink>{' '}
          for full coverage.
        </Box>
      </Box>
    </Flex>

    {/* Toolbar */}
    <MockToolbar />

    {/* Table area */}
    <Box
      position="relative"
      flex="1"
      overflow="hidden"
      borderTop="1px solid"
      borderTopColor="kgray.200"
    >
      {/* Table header — sharp */}
      <MockTableHeader />

      {/* Table body — blurred */}
      <Box filter="blur(3px)" opacity={0.6} pointerEvents="none">
        <MockTableBody />
      </Box>

      {/* Semi-transparent overlay */}
      <Box
        position="absolute"
        top="32px"
        left={0}
        right={0}
        bottom={0}
        bg="rgba(255, 255, 255, 0.4)"
        pointerEvents="none"
      />

      {/* Centered info overlay */}
      <Flex
        position="absolute"
        top="32px"
        left={0}
        right={0}
        bottom={0}
        alignItems="center"
        justifyContent="center"
      >
        <Box textAlign="center">
          {/* Icon with checkmark badge */}
          <Flex justifyContent="center" mb={5}>
            <Box position="relative" display="inline-block">
              <Image src="/web-intent.svg" boxSize="80px" alt="" />
              <Circle
                size="28px"
                bg="kred.400"
                color="white"
                position="absolute"
                top="-4px"
                right="-4px"
                border="3px solid white"
              >
                <Checkmark size={16} />
              </Circle>
            </Box>
          </Flex>

          <Text fontSize="xl" fontWeight="semibold" mb={3}>
            Your script is verified and data is flowing!
          </Text>
          <Text color="kgray.300" fontSize="md" maxW="368px" mx="auto">
            We&apos;ll email you as soon as your engaged accounts have populated
            in Keyplay.
          </Text>
        </Box>
      </Flex>
    </Box>
  </Flex>
);
